import { Paper, Stack, Typography, Box, Chip, Link, Tooltip, CircularProgress } from '@mui/material';

// #108 Phase 1 — 제품 축 대시보드의 카드 껍데기.
//
// 🔴 카드마다 출처와 최신 시각을 반드시 표시한다.
//   보이지 않으면 오래된 값을 현재로 읽는다. 이건 스타일이 아니라 요구사항이다.
//
// 🔴 데이터가 없는 축은 카드를 숨기지 않는다.
//   숨기면 "그 제품은 해당 없음" 과 "아직 수집되지 않음" 이 구별되지 않는다.

export function AxisCard({ title, icon, source, sourceUrl, updatedAt, stale, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        {icon}
        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{title}</Typography>
        <Box sx={{ flex: 1 }} />
        {action}
      </Stack>

      <Box sx={{ flex: 1 }}>{children}</Box>

      {/* 출처 · 최신 시각 — 필수 */}
      <Stack direction="row" flexWrap="wrap" alignItems="center" gap={0.75}
        sx={{ pt: 1, borderTop: 1, borderColor: 'divider', fontSize: 11.5, color: 'text.disabled' }}>
        <span>출처</span>
        {sourceUrl
          ? <Link href={sourceUrl} target="_blank" rel="noopener" sx={{ fontSize: 11.5, fontFamily: 'monospace' }}>{source}</Link>
          : <Box component="span" sx={{ fontFamily: 'monospace' }}>{source}</Box>}
        <Box sx={{ flex: 1 }} />
        <span>기준 {updatedAt || '—'}</span>
        {stale && (
          <Tooltip title="수집 주기를 넘겼습니다 — 현재 상태가 아닐 수 있습니다">
            <Chip size="small" color="warning" variant="outlined" label="오래됨" sx={{ height: 17, fontSize: 10 }} />
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

// 아직 수집되지 않은 축. 빈 값을 0 으로 그리면 "깨끗함" 으로 읽히므로 문장으로 말한다.
export function NotCollected({ reason }) {
  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }}>
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary', fontWeight: 600 }}>
        아직 수집되지 않았습니다
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.disabled', lineHeight: 1.6 }}>
        {reason}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>
        이 값은 <b>0 이 아니라 미상</b>입니다 — 문제가 없다는 뜻이 아닙니다.
      </Typography>
    </Stack>
  );
}

export function AxisLoading() {
  return (
    <Stack alignItems="center" sx={{ py: 2.5 }}>
      <CircularProgress size={20} />
    </Stack>
  );
}

// 축 하나가 실패해도 페이지 전체를 막지 않는다 — 실패 사실은 그대로 보여준다.
export function AxisError({ message, hint }) {
  return (
    <Stack spacing={0.5} sx={{ py: 1.5 }}>
      <Typography sx={{ fontSize: 13.5, color: 'error.main', fontWeight: 600 }}>
        {message || '불러오지 못했습니다'}
      </Typography>
      {hint && <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>{hint}</Typography>}
    </Stack>
  );
}

// 숫자 타일 한 줄. label 아래 값. flagged 는 0 이 아닐 때 색이 붙는다.
export function CountRow({ items }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={2} sx={{ py: 0.5 }}>
      {items.map((it) => (
        <Box key={it.label} sx={{ minWidth: 58 }}>
          <Typography sx={{
            fontSize: 22, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.15,
            color: it.color || (it.flag && it.value > 0 ? 'warning.main' : 'text.primary'),
          }}>
            {it.value ?? '—'}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>{it.label}</Typography>
        </Box>
      ))}
    </Stack>
  );
}
